import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Loader2,
  Plus,
  History,
  Database,
  ClipboardList,
  RefreshCw,
} from 'lucide-react'
import {
  ImportForm,
  ImportHistoryList,
  ImportSummaryBar,
  SnapshotList,
  SnapshotComparison,
  RawRecordsTable,
} from '@/components/attendance-metrics'
import {
  useImportHistory,
  useImportJob,
  useStartImport,
  useCancelImport,
  useSnapshots,
  useDeleteSnapshot,
  useCompareSnapshots,
  useRawRecordsCount,
  useDeleteRawRecords,
} from '@/hooks/useAttendanceMetrics'
import { toast } from 'sonner'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useQueryClient } from '@tanstack/react-query'

export function AttendanceMetricsManagementPage() {
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  // Tab state with URL sync
  const tabFromUrl = searchParams.get('tab') || 'imports'
  const [activeTab, setActiveTab] = useState(tabFromUrl)

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab !== tabFromUrl) {
      setSearchParams({ tab: activeTab }, { replace: true })
    }
  }, [activeTab, tabFromUrl, setSearchParams])

  // Update state when URL changes (browser back/forward)
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl])

  // Modals state
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importInitialDates, setImportInitialDates] = useState<{
    startDate: string
    endDate: string
  } | null>(null)
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false)
  const [selectedSnapshots, setSelectedSnapshots] = useState<string[]>([])
  const [messagesRefreshKey, setMessagesRefreshKey] = useState(0)

  // Confirmation dialogs state
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const [confirmRetryOpen, setConfirmRetryOpen] = useState(false)
  const [confirmDeleteSnapshotOpen, setConfirmDeleteSnapshotOpen] = useState(false)
  const [pendingImportId, setPendingImportId] = useState<string | null>(null)
  const [pendingRetryPeriod, setPendingRetryPeriod] = useState<{
    startDate: string
    endDate: string
  } | null>(null)
  const [pendingSnapshotId, setPendingSnapshotId] = useState<string | null>(null)

  // Active import job (for polling)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)

  // Queries
  const importsQuery = useImportHistory()
  const activeJobQuery = useImportJob(activeJobId || '', {
    enabled: !!activeJobId,
    refetchInterval: activeJobId ? 2000 : undefined,
  })
  const snapshotsQuery = useSnapshots()
  const rawRecordsCountQuery = useRawRecordsCount()

  // Comparison query (only when 2 snapshots selected)
  const comparisonQuery = useCompareSnapshots(selectedSnapshots[0] || '', selectedSnapshots[1] || '', {
    enabled: selectedSnapshots.length === 2,
  })

  // Mutations
  const startImportMutation = useStartImport()
  const cancelImportMutation = useCancelImport()
  const deleteSnapshotMutation = useDeleteSnapshot()
  const deleteRawRecordsMutation = useDeleteRawRecords()

  // Handlers
  const handleStartImport = async (data: {
    startDate: string
    endDate: string
    forceReimport?: boolean
  }) => {
    try {
      const result = await startImportMutation.mutateAsync(data)
      setActiveJobId(result.id)
      setImportModalOpen(false)
      toast.success('Importação iniciada!')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao iniciar importação')
    }
  }

  const handleCancelImport = (importId: string) => {
    setPendingImportId(importId)
    setConfirmCancelOpen(true)
  }

  const confirmCancelImport = async () => {
    if (!pendingImportId) return
    try {
      await cancelImportMutation.mutateAsync(pendingImportId)
      if (activeJobId === pendingImportId) {
        setActiveJobId(null)
      }
      toast.success('Importação cancelada')
      importsQuery.refetch()
    } catch (error) {
      toast.error('Erro ao cancelar importação')
    } finally {
      setPendingImportId(null)
      setConfirmCancelOpen(false)
    }
  }

  const handleRetryImport = (startDate: string, endDate: string) => {
    setPendingRetryPeriod({ startDate, endDate })
    setConfirmRetryOpen(true)
  }

  const handleDuplicateImport = (startDate: string, endDate: string) => {
    setImportInitialDates({ startDate, endDate })
    setImportModalOpen(true)
  }

  const confirmRetryImport = async () => {
    if (!pendingRetryPeriod) return
    try {
      const result = await startImportMutation.mutateAsync({
        startDate: pendingRetryPeriod.startDate,
        endDate: pendingRetryPeriod.endDate,
      })
      setActiveJobId(result.id)
      toast.success('Importação reiniciada!')
      importsQuery.refetch()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao reiniciar importação')
    } finally {
      setPendingRetryPeriod(null)
      setConfirmRetryOpen(false)
    }
  }

  const handleDeleteSnapshot = (id: string) => {
    setPendingSnapshotId(id)
    setConfirmDeleteSnapshotOpen(true)
  }

  const confirmDeleteSnapshot = async () => {
    if (!pendingSnapshotId) return
    try {
      await deleteSnapshotMutation.mutateAsync(pendingSnapshotId)
      toast.success('Relatório excluído')
    } catch (error) {
      toast.error('Erro ao excluir relatório')
    } finally {
      setPendingSnapshotId(null)
      setConfirmDeleteSnapshotOpen(false)
    }
  }

  const handleCompareSnapshots = () => {
    if (selectedSnapshots.length === 2) {
      setComparisonModalOpen(true)
    }
  }

  const handleDeleteRawRecords = async (beforeDate: string) => {
    try {
      const result = await deleteRawRecordsMutation.mutateAsync({ beforeDate })
      toast.success(`${result.deletedCount} registros removidos`)
    } catch (error) {
      toast.error('Erro ao limpar dados')
    }
  }

  // Stop polling when job completes
  if (activeJobQuery.data?.status === 'completed' || activeJobQuery.data?.status === 'failed') {
    if (activeJobId) {
      setActiveJobId(null)
      if (activeJobQuery.data.status === 'completed') {
        toast.success('Importação concluída!')
        // Invalidate metrics queries on dashboard
        queryClient.invalidateQueries({ queryKey: ['attendance-metrics'] })
      } else {
        toast.error('Importação falhou: ' + (activeJobQuery.data.errorMessage || 'Erro desconhecido'))
      }
    }
  }

  // Loading state for initial data
  const isInitialLoading = importsQuery.isLoading && snapshotsQuery.isLoading

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando dados...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Gestão de Métricas</h1>
        <p className="text-muted-foreground">
          Gerenciar importações, relatórios e dados brutos
        </p>
      </div>

      {/* Tabs: Importações | Relatórios | Dados Brutos */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="imports" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Importações</span>
            <span className="sm:hidden">Import.</span>
          </TabsTrigger>
          <TabsTrigger value="snapshots" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            <span className="hidden sm:inline">Relatórios</span>
            <span className="sm:hidden">Relat.</span>
          </TabsTrigger>
          <TabsTrigger value="messages" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">Mensagens</span>
            <span className="sm:hidden">Msgs</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Importações */}
        <TabsContent value="imports" className="space-y-6">
          {/* Tab header with action */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Histórico de Importações</h2>
              <p className="text-sm text-muted-foreground">
                Visualize e gerencie as importações de tickets do Whaticket
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setImportModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Importação
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  importsQuery.refetch()
                  rawRecordsCountQuery.refetch()
                }}
                disabled={importsQuery.isFetching || rawRecordsCountQuery.isFetching}
                title="Atualizar lista"
              >
                {importsQuery.isFetching || rawRecordsCountQuery.isFetching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Summary bar with total messages and cleanup */}
          <ImportSummaryBar
            rawRecordsCount={rawRecordsCountQuery.data}
            onClear={handleDeleteRawRecords}
            isClearing={deleteRawRecordsMutation.isPending}
            isLoading={rawRecordsCountQuery.isLoading}
          />

          <ImportHistoryList
            imports={importsQuery.data || []}
            onRetry={handleRetryImport}
            onCancel={handleCancelImport}
            onDuplicate={handleDuplicateImport}
            isLoading={importsQuery.isLoading}
          />
        </TabsContent>

        {/* TAB 2: Relatórios (Snapshots) */}
        <TabsContent value="snapshots" className="space-y-6">
          {/* Tab header with action */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Relatórios Consolidados</h2>
              <p className="text-sm text-muted-foreground">
                Crie e compare relatórios de métricas por período
              </p>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => snapshotsQuery.refetch()}
              disabled={snapshotsQuery.isFetching}
              title="Atualizar lista"
            >
              {snapshotsQuery.isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
          <SnapshotList
            snapshots={snapshotsQuery.data || []}
            selectedIds={selectedSnapshots}
            onSelectionChange={setSelectedSnapshots}
            onCompare={handleCompareSnapshots}
            onDelete={handleDeleteSnapshot}
            isLoading={snapshotsQuery.isLoading}
          />
        </TabsContent>

        {/* TAB 3: Mensagens (lazy loaded to avoid triple query) */}
        <TabsContent value="messages" className="space-y-6">
          {/* Tab header with action */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Mensagens</h2>
              <p className="text-sm text-muted-foreground">
                Visualize e filtre mensagens importadas do Whaticket
              </p>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setMessagesRefreshKey((prev) => prev + 1)}
              title="Atualizar lista"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Messages Table - only mount when tab is active */}
          {activeTab === 'messages' && <RawRecordsTable refreshKey={messagesRefreshKey} />}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {isDesktop ? (
        <Dialog
          open={importModalOpen}
          onOpenChange={(open) => {
            setImportModalOpen(open)
            if (!open) setImportInitialDates(null)
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nova Importação</DialogTitle>
              <DialogDescription>Importar tickets fechados do Whaticket</DialogDescription>
            </DialogHeader>
            <ImportForm
              onSubmit={handleStartImport}
              existingPeriods={(importsQuery.data || []).map((i) => ({
                startDate: i.startDate,
                endDate: i.endDate,
              }))}
              isSubmitting={startImportMutation.isPending}
              initialStartDate={importInitialDates?.startDate}
              initialEndDate={importInitialDates?.endDate}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet
          open={importModalOpen}
          onOpenChange={(open) => {
            setImportModalOpen(open)
            if (!open) setImportInitialDates(null)
          }}
        >
          <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Nova Importação</SheetTitle>
              <SheetDescription>Importar tickets fechados do Whaticket</SheetDescription>
            </SheetHeader>
            <div className="mt-6">
              <ImportForm
                onSubmit={handleStartImport}
                existingPeriods={(importsQuery.data || []).map((i) => ({
                  startDate: i.startDate,
                  endDate: i.endDate,
                }))}
                isSubmitting={startImportMutation.isPending}
                initialStartDate={importInitialDates?.startDate}
                initialEndDate={importInitialDates?.endDate}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}

      <SnapshotComparison
        isOpen={comparisonModalOpen}
        onClose={() => setComparisonModalOpen(false)}
        comparison={comparisonQuery.data || null}
      />

      {/* Confirmation Dialogs */}
      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Importação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar esta importação? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Não, manter</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancelImport}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sim, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRetryOpen} onOpenChange={setConfirmRetryOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reprocessar Importação</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja reprocessar os dados deste período? Os dados existentes serão atualizados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRetryImport}>Reprocessar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDeleteSnapshotOpen} onOpenChange={setConfirmDeleteSnapshotOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Relatório</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este relatório consolidado? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSnapshot}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
